import {
  DashboardState,
  EAppSupportType,
  EProjectType,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IComponent,
  IDisplayItem,
  IDisplayItemProperty,
  IEvent,
  IProperty,
  isAppSupportTypeOfferedOnScreen,
  ISymbol,
  ITargetScreenInputParam
} from "@kuika/kuika-cl-model";
import { isReactNativeGenerationOffered } from "../../components/design-tools/app-settings/react-native-feature-gate";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { ROOT_SECTIONS } from "./designer-style-actions";
import { COMPONENT_PURPOSES } from "./mcp-tools/component-purposes";
import { resolveProjectStyles, simplifyStyleObj } from "./mcp-tools/helpers/style-helpers";
import { ScreenIndex, buildScreenIndex, collectNavigationTargets } from "./navigation-target-collector";

// -- Result interfaces --

interface ITreeNode {
  kuikaId: string | undefined;
  name: string | undefined;
  componentType: string | undefined;
  friendlyName: string | undefined;
  childCount: number;
  navigatesTo?: string;
  children?: ITreeNode[];
}

interface IPropertySummaryItem {
  name: string;
  type: number;
  isBound: boolean;
  // For unbound fixed-value properties, the literal value currently set
  // (e.g. "0" on a KPI placeholder Label, "Shares" on a header Label).
  // null for bound properties — callers should read the binding
  // separately. Exposed so the Binder can distinguish placeholder
  // targets from fixed-text neighbors without a second tool call.
  value: string | null;
  // KOA-17592 — the bound datasource ACTION (name/path) for a data/options-bag
  // property (KanbanBoard cardOptions/laneOptions, chart/calendar options, a
  // SelectBox datasource, …). These bindings live on the symbol's
  // actionDefinition* fields, NOT the value-binding `category` that drives
  // `isBound` — so a bound board still reports `isBound:false, value:null` and
  // the missing-binding failure is otherwise INVISIBLE. Present only when an
  // action is actually bound (omitted otherwise, keeping get_info lean), so a
  // pipeline audit can flag a KanbanBoard whose cardOptions/laneOptions never
  // got their datasource.
  dataAction?: string;
  // KOA-18136 §94b — WHAT a bound value actually shows.
  //
  // `value` is null for a bound property (the literal belongs to unbound ones), so a caller could
  // see THAT a component is bound and never WHICH value it displays. That gap is why "this field is
  // already on the screen" could only be asked of a component's id or its caption: a Label bound to
  // `{current.SupplierName}` carries neither. Read straight off the symbol, present only when the
  // symbol has it, so nothing changes for a property that has neither.
  boundField?: string;
  boundInputName?: string;
}

interface IEventSummaryItem {
  eventName: string;
  actionCount: number;
}

/** One action parameter (or navigate screen-input) and a short description of what feeds it. */
export interface IActionParameterSummary {
  name: string;
  value: string;
}

/**
 * Compact read-back of what an action instance is wired to. Every field is
 * omitted when empty so row projections stay small.
 */
export interface IActionBindingSummary {
  parameters?: IActionParameterSummary[];
  /** NAVIGATE only — the resolved `toScreenId`. */
  targetScreenId?: string;
  /** NAVIGATE only — present when `targetScreenId` matches a screen in the project. */
  targetScreenName?: string;
  /** NAVIGATE only — the target screen's input bindings (`targetScreenInputParams`). */
  screenInputs?: IActionParameterSummary[];
}

interface IActionSummary extends IActionBindingSummary {
  id: string;
  label: string;
  description: string;
  actionDefinitionId: string | undefined | null;
}

interface IEventDetail {
  eventName: string;
  actionCount: number;
  actions: IActionSummary[];
}

interface IComponentListItem {
  name: string;
  category: string | null;
  purpose: string | null;
  isContainer: boolean;
  isFormField: boolean;
  keyProperties: string[];
  availableEvents: string[];
}

interface IChildSummary {
  kuikaId: string | undefined;
  name: string | undefined;
  componentType: string | undefined;
  friendlyName: string | null;
}

interface IAncestorChainEntry {
  kuikaId: string;
  name: string;
  friendlyName: string | null;
}

/** The contract channels a display item carries, omitting the unset ones. */
export interface IContractChannelProjection {
  contractId?: string;
  dataRef?: string;
  fieldRef?: string;
  interactionRef?: string;
}

export function contractChannelProjection(di: IDisplayItem): IContractChannelProjection {
  const projection: IContractChannelProjection = {};
  if (di.contractId) projection.contractId = di.contractId;
  if (di.dataRef) projection.dataRef = di.dataRef;
  if (di.fieldRef) projection.fieldRef = di.fieldRef;
  if (di.interactionRef) projection.interactionRef = di.interactionRef;
  return projection;
}

interface IFoundComponent {
  kuikaId: string | undefined;
  /** Catalog type name (same as item.name — e.g. TextInput, Table). */
  name: string | undefined;
  /**
   * Explicit type field for ownership / dual-shell rehydration (KOA-17797).
   * Mirrors `name` so consumers that read `componentType` (render-expander,
   * inventory seed) do not see form fields≈0 while the canvas has inputs.
   */
  componentType: string | undefined;
  friendlyName: string | null;
  /**
   * Screen-contract channels (`contractId` + `dataRef` / `fieldRef` /
   * `interactionRef`).
   *
   * `list_all_components` is the canvas inventory the deterministic Wirer /
   * Binder reads, so it is the primary read path for these: a component whose
   * contract id cannot be read back from the live canvas cannot be resolved at
   * all, and stamping it would have been pointless.
   *
   * OMITTED rather than nulled when absent. This projection enumerates every
   * component on the screen, and today every one of them is channel-less — four
   * `null`s per component would be pure prompt weight on a call whose whole
   * purpose is to be cheap enough to run over a full canvas.
   */
  contractId?: string;
  dataRef?: string;
  fieldRef?: string;
  interactionRef?: string;
  /**
   * Action alias bound to this component's `dataSource` property, or null when
   * it has none (non-repeater, unbound, or a fixed option list).
   *
   * KOA-18227 — the orchestrator's dual-shell gate identifies a list host on a
   * screen by (component family, bound datasource): a second Table over the
   * SAME action is a duplicated list, a second Table over a DIFFERENT action is
   * a legitimate second list. Without this field the inventory cannot tell them
   * apart and the gate has to fail closed on the family alone.
   */
  dataSourceAlias: string | null;
}

/**
 * Action alias on a component's `dataSource` symbol. `actionDefinitionObjectPath`
 * is what `setDesignerPropertyDataAction` writes when an action is bound; a
 * fixed option list clears it (and sets `isFixed`), which reads as unbound here.
 */
function readDataSourceAlias(di: IDisplayItem): string | null {
  const symbol = di.item?.properties?.find((p: IDisplayItemProperty) => p.name === "dataSource")?.symbol;
  const alias: unknown = symbol?.actionDefinitionObjectPath;
  if (typeof alias !== "string") return null;
  const trimmed: string = alias.trim();
  return trimmed.length > 0 ? trimmed : null;
}

interface ICompletenessIssue {
  kuikaId: string;
  componentType: string;
  friendlyName: string | null;
  severity: "high" | "medium" | "low";
  issue: string;
  suggestion: string;
  /**
   * Advisory only: the unwired button looks like a navigation menu's entry for the
   * OPEN screen. Reported, but excluded from `complete` and `score`.
   */
  likelyCurrentScreenEntry?: true;
}

interface ICompletenessResult {
  complete: boolean;
  score: number;
  componentCount: number;
  issues: ICompletenessIssue[];
  summary: string;
}

// -- Functions --

export function buildDesignerTreeNode(di: IDisplayItem, screenIndex?: ScreenIndex): ITreeNode {
  const node: ITreeNode = {
    kuikaId: di.id?.toString(),
    name: di.item?.name,
    componentType: di.item?.name,
    friendlyName: di.friendlyName || undefined,
    childCount: di.children?.length || 0
  };

  if (screenIndex && screenIndex.byId.size > 0) {
    const navTargets = collectNavigationTargets(di, screenIndex);
    if (navTargets) node.navigatesTo = navTargets;
  }

  if (di.children && di.children.length > 0) {
    node.children = di.children.map((child: IDisplayItem) => buildDesignerTreeNode(child, screenIndex));
  }

  return node;
}

export function buildDesignerTextTree(
  displayItems: IDisplayItem[],
  screenName: string,
  maxDepth: number = 10,
  includeStyles: boolean = false,
  screenIndex?: ScreenIndex
): string {
  const lines: string[] = [`Screen: "${screenName}"`];

  function walk(di: IDisplayItem, depth: number) {
    const indent = "  ".repeat(depth);
    const name = di.item?.name || "Unknown";
    const friendly = di.friendlyName ? ` "${di.friendlyName}"` : "";
    const id = di.id != null ? ` [${di.id}]` : "";
    let navSuffix = "";
    if (screenIndex && screenIndex.byId.size > 0) {
      const navTargets = collectNavigationTargets(di, screenIndex);
      if (navTargets) navSuffix = ` →${navTargets}`;
    }
    let styleSuffix = "";
    if (includeStyles && di.styleObj) {
      const styles = simplifyStyleObj(di.styleObj);
      if (styles && typeof styles === "object") {
        const entries = Object.entries(styles).filter(([, v]) => v != null && v !== "");
        if (entries.length > 0) {
          styleSuffix = ` style={${entries.map(([k, v]) => `${k}:${v}`).join("; ")}}`;
        }
      }
    }
    lines.push(`${indent}${name}${friendly}${id}${navSuffix}${styleSuffix}`);

    const children = di.children;
    if (children && children.length > 0) {
      if (depth >= maxDepth) {
        lines.push(`${indent}  ...${children.length} children`);
      } else {
        for (const child of children) {
          walk(child, depth + 1);
        }
      }
    }
  }

  for (const di of displayItems) {
    walk(di, 0);
  }

  return lines.join("\n");
}

export function getDesignerPropertySummary(di: IDisplayItem, sd?: IScreenDesignContextState): IPropertySummaryItem[] {
  if (!di.item?.properties) return [];

  let componentDefProperties: IProperty[] | null = null;
  const projectType = sd?.project?.projectType;
  const isWebScreen = (sd as any)?.getIsWebScreen?.() ?? false;
  if (sd) {
    const componentDefs = sd.project?.componentSetObj?.components;
    if (componentDefs) {
      const componentDef = componentDefs.find((component: IComponent) => component.id === di.item.id);
      componentDefProperties = componentDef?.properties || null;
    }
  }

  return di.item.properties
    .filter((property: IDisplayItemProperty) => {
      if (property.selectedType === 3) return false;
      if (property.isDeprecated === true) return false;
      if (componentDefProperties) {
        const defProperty = componentDefProperties.find((item: IProperty) => item.name === property.name);
        if (defProperty?.isTechnical === true || defProperty?.isDeprecated === true) return false;
        // Same platform gate the property panel applies in property-form.tsx —
        // drop Web-only properties when editing a Mobile project's screen, and
        // Mobile-only properties when editing a Desktop project's screen, so
        // the LLM never sees a property the designer itself hides.
        if (
          !isAppSupportTypeOfferedOnScreen({
            appSupportType: defProperty?.appSupportType,
            projectType,
            isWebScreen,
            reactNativeOffered: isReactNativeGenerationOffered()
          })
        ) {
          return false;
        }
      }
      return true;
    })
    .map((property: IDisplayItemProperty) => {
      const isBound = property.symbol?.category !== undefined && property.symbol?.category !== 4;
      const fixedValue = property.symbol?.fixedValue;
      const value =
        !isBound &&
        (typeof fixedValue === "string" || typeof fixedValue === "number" || typeof fixedValue === "boolean")
          ? String(fixedValue)
          : null;
      // KOA-17592 — surface the bound datasource action for data/options-bag
      // properties. Precedence mirrors getSelectedDataAction: a fixed option
      // set is NOT a datasource; otherwise the action path / result-action id.
      const symbol = property.symbol;
      const boundAction: string | null =
        symbol && symbol.isFixed !== true
          ? symbol.actionDefinitionObjectPath || symbol.resultActionId || symbol.actionDefinitionId || null
          : null;
      const boundField: string | null =
        isBound && typeof symbol?.fieldName === "string" && symbol.fieldName.trim().length > 0
          ? symbol.fieldName.trim()
          : null;
      const boundInputName: string | null =
        isBound && typeof symbol?.inputName === "string" && symbol.inputName.trim().length > 0
          ? symbol.inputName.trim()
          : null;
      return {
        name: property.name,
        type: property.selectedType,
        isBound,
        value,
        ...(boundAction ? { dataAction: String(boundAction) } : {}),
        ...(boundField ? { boundField } : {}),
        ...(boundInputName ? { boundInputName } : {})
      };
    });
}

interface IPropertyValueItem {
  name: string;
  value: unknown;
  type: number;
  isBound: boolean;
}

export function getDesignerPropertyValues(
  sdInstance: ScreenDesignInstance,
  input: { kuikaId?: string; propertyNames?: string[] }
): { kuikaId: string; properties: IPropertyValueItem[] } | { error: string } {
  const { kuikaId, propertyNames } = input;
  if (!kuikaId) return { error: "kuikaId is required" };
  if (!propertyNames || !Array.isArray(propertyNames) || propertyNames.length === 0) {
    return { error: "propertyNames array is required" };
  }

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };
  if (!di.item?.properties) return { kuikaId, properties: [] };

  const nameSet = new Set(propertyNames.map((n) => n.toLowerCase()));
  const properties: IPropertyValueItem[] = di.item.properties
    .filter((property: IDisplayItemProperty) => nameSet.has(property.name.toLowerCase()))
    .map((property: IDisplayItemProperty) => ({
      name: property.name,
      value: property.symbol?.fixedValue ?? property.value ?? null,
      type: property.selectedType,
      isBound: property.symbol?.category !== undefined && property.symbol?.category !== 4
    }));

  return { kuikaId, properties };
}

export function getDesignerAvailableEvents(di: IDisplayItem, kuikaId?: string): string[] {
  if (kuikaId && ROOT_SECTIONS[kuikaId]) {
    return ROOT_SECTIONS[kuikaId].availableEvents;
  }
  if (!di.item?.properties) return [];
  return di.item.properties
    .filter((property: IDisplayItemProperty) => property.selectedType === 3)
    .map((property: IDisplayItemProperty) => property.name);
}

export function getDesignerEventSummary(di: IDisplayItem): IEventSummaryItem[] {
  if (!di.item?.events) return [];
  return di.item.events.map((event: IEvent) => ({
    eventName: event.eventName,
    actionCount: event.actions?.length || 0
  }));
}

import { getScreenDictionary as getScreenDesignDictionary, getScreenDesign } from "./designer-utils";

export function getDesignerScreenTree(
  sdInstance: ScreenDesignInstance,
  input: { maxDepth?: string | number; includeStyles?: boolean | string } = {}
): string | { error: string } {
  const sd = getValues(sdInstance);
  const screenDesign = getScreenDesign(sd);
  if (!screenDesign || !screenDesign.displayItems) {
    return { error: "No screen design loaded" };
  }

  const maxDepth = Math.max(1, Math.min(20, parseInt(String(input?.maxDepth ?? 5), 10) || 5));
  const includeStyles = input?.includeStyles === true || input?.includeStyles === "true";
  const screenIndex = buildScreenIndex(getScreenListFromInstance(sdInstance));
  return buildDesignerTextTree(screenDesign.displayItems, screenDesign.name, maxDepth, includeStyles, screenIndex);
}

function getScreenListFromInstance(sdInstance: ScreenDesignInstance): { id: string; name: string }[] | undefined {
  const provider = (sdInstance as any)?.screenProvider ?? (sdInstance as any)?.projectProvider;
  const list = provider?.state?.screenList;
  if (!Array.isArray(list)) return undefined;
  return list
    .map((entry: any) => ({
      id: typeof entry?.id === "string" ? entry.id : "",
      name: typeof entry?.name === "string" ? entry.name : ""
    }))
    .filter((entry: { id: string; name: string }) => entry.id.length > 0 && entry.name.length > 0);
}

interface IComponentInfoResult extends IContractChannelProjection {
  kuikaId: string | undefined;
  name: string | undefined;
  componentType: string | undefined;
  friendlyName: string | null;
  parentId: number | undefined;
  properties: IPropertySummaryItem[];
  propertiesOmitted?: number;
  events: IEventSummaryItem[];
  availableEvents: string[];
  childCount: number;
  hasChildren: boolean;
  visibility: unknown;
  editability: unknown;
}

const MAX_PROPERTIES = 15;
const KEY_PROPERTY_NAMES = new Set([
  "value",
  "label",
  "placeholder",
  "visible",
  "enabled",
  "options",
  "datasource",
  "datavaluefield",
  "datatextfield",
  "text",
  "title",
  "src",
  "checked",
  "iconName",
  "size",
  "tab",
  "count"
]);

function capProperties(allProperties: IPropertySummaryItem[]): {
  properties: IPropertySummaryItem[];
  propertiesOmitted?: number;
} {
  if (allProperties.length <= MAX_PROPERTIES) {
    return { properties: allProperties };
  }
  const key = allProperties.filter((p) => KEY_PROPERTY_NAMES.has(p.name));
  const rest = allProperties.filter((p) => !KEY_PROPERTY_NAMES.has(p.name));
  const properties = [...key, ...rest.slice(0, MAX_PROPERTIES - key.length)];
  return { properties, propertiesOmitted: allProperties.length - properties.length };
}

export function getDesignerComponentInfo(
  sdInstance: ScreenDesignInstance,
  kuikaId?: string
): IComponentInfoResult | { error: string } {
  if (!kuikaId) return { error: "kuikaId is required" };

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };

  const allProperties = getDesignerPropertySummary(di, sd);
  const { properties, propertiesOmitted } = capProperties(allProperties);

  return {
    kuikaId: di.id?.toString(),
    name: di.item.name,
    componentType: di.item.name,
    friendlyName: di.friendlyName || null,
    ...contractChannelProjection(di),
    parentId: di.parentId,
    properties,
    ...(propertiesOmitted !== undefined ? { propertiesOmitted } : {}),
    events: getDesignerEventSummary(di),
    availableEvents: getDesignerAvailableEvents(di, kuikaId),
    childCount: di.children?.length || 0,
    hasChildren: (di.children?.length || 0) > 0,
    visibility:
      di.item?.properties?.find((property: IDisplayItemProperty) => property.name === "visible")?.symbol?.fixedValue ??
      null,
    editability:
      di.item?.properties?.find((property: IDisplayItemProperty) => property.name === "enabled")?.symbol?.fixedValue ??
      null
  };
}

interface ISelectedComponentResult extends IContractChannelProjection {
  kuikaId: string | undefined;
  name: string | undefined;
  componentType: string | undefined;
  friendlyName: string | null;
  properties: IPropertySummaryItem[];
  propertiesOmitted?: number;
  events: IEventSummaryItem[];
  availableEvents: string[];
  visibility: unknown;
}

export function getDesignerSelectedComponent(
  sdInstance: ScreenDesignInstance
): { selected: null } | ISelectedComponentResult {
  const sd = getValues(sdInstance);
  const screenDesign = getScreenDesign(sd);
  const di: IDisplayItem | undefined = sd.getSelectedDisplayItem?.(screenDesign);
  if (!di) return { selected: null };

  const allProperties = getDesignerPropertySummary(di, sd);
  const { properties, propertiesOmitted } = capProperties(allProperties);

  return {
    kuikaId: di.id?.toString(),
    name: di.item.name,
    componentType: di.item.name,
    friendlyName: di.friendlyName || null,
    ...contractChannelProjection(di),
    properties,
    ...(propertiesOmitted !== undefined ? { propertiesOmitted } : {}),
    events: getDesignerEventSummary(di),
    availableEvents: getDesignerAvailableEvents(di, di.id?.toString()),
    visibility:
      di.item?.properties?.find((property: IDisplayItemProperty) => property.name === "visible")?.symbol?.fixedValue ??
      null
  };
}

export function listDesignerAvailableComponents(
  sdInstance: ScreenDesignInstance,
  input: { filter?: string } = {}
): { components: IComponentListItem[] } | { error: string } {
  const sd = getValues(sdInstance);
  const components: IComponent[] | undefined = sd.project?.componentSetObj?.components;
  if (!components) return { error: "Components not loaded" };

  const projectType = sd.project?.projectType;
  const isWebScreen = sd.getIsWebScreen?.() ?? true;
  const dashboardState = sd.dashboardState ?? null;
  const isReportMode = dashboardState === DashboardState.reportDesigner;

  const filtered = components.filter((component: IComponent) => {
    if (
      component.isItChildItem ||
      component.isItNavigationComponent ||
      component.isItHiddenFromComponentList ||
      component.isDeprecated
    ) {
      return false;
    }
    if (
      !isAppSupportTypeOfferedOnScreen({
        appSupportType: component.appSupportType,
        projectType,
        isWebScreen,
        reactNativeOffered: isReactNativeGenerationOffered()
      })
    ) {
      return false;
    }
    if (isReportMode && !component.isReportDesignComponent) return false;
    if (!isReportMode && component.isReportDesignComponent) return false;
    return true;
  });

  const seen = new Set<string>();
  let list: IComponentListItem[] = [];
  for (const component of filtered) {
    if (seen.has(component.name)) continue;
    seen.add(component.name);

    const purposeKey = isReportMode ? `${component.name}__report` : component.name;
    const purpose = COMPONENT_PURPOSES[purposeKey] || COMPONENT_PURPOSES[component.name] || null;
    const isContainer = component.blockStyle === 2;
    const isFormField = component.wrapWithFormItem || false;

    const keyProperties = (component.properties || [])
      .filter((property: IProperty) => {
        if (property.isTechnical || property.isDeprecated || property.selectedType === 3) return false;
        if (property.appSupportType === EAppSupportType.Web && projectType !== EProjectType.Desktop && !isWebScreen)
          return false;
        if (property.appSupportType === EAppSupportType.Mobile && projectType === EProjectType.Desktop) return false;
        return true;
      })
      .slice(0, 5)
      .map((property: IProperty) => property.name);

    const availableEvents = (component.properties || [])
      .filter((property: IProperty) => property.selectedType === 3)
      .map((property: IProperty) => property.name)
      .slice(0, 5);

    list.push({
      name: component.name,
      category: component.componentCategory || null,
      purpose,
      isContainer,
      isFormField,
      keyProperties,
      availableEvents
    });
  }

  if (input.filter) {
    const filterLower = input.filter.toLowerCase();
    list = list.filter(
      (component: IComponentListItem) =>
        component.name.toLowerCase().includes(filterLower) ||
        (component.category && component.category.toLowerCase().includes(filterLower)) ||
        (component.purpose && component.purpose.toLowerCase().includes(filterLower))
    );
  }

  return { components: list };
}

export function getDesignerChildren(
  sdInstance: ScreenDesignInstance,
  kuikaId?: string
): { kuikaId: string; childCount: number; children: IChildSummary[] } | { error: string } {
  if (!kuikaId) return { error: "kuikaId is required" };

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };

  const children: IChildSummary[] = (di.children || []).map((child: IDisplayItem) => ({
    kuikaId: child.id?.toString(),
    name: child.item?.name,
    componentType: child.item?.name,
    friendlyName: child.friendlyName || null
  }));

  return { kuikaId, childCount: children.length, children };
}

const BINDING_SUMMARY_MAX_LENGTH: number = 60;
const TO_SCREEN_ID_PARAMETER: string = "toScreenId";

interface IScreenListEntry {
  id: string;
  name?: string;
}

interface INamedScreen {
  id: string;
  name: string;
}

interface IScreenListState {
  screenList?: IScreenListEntry[];
}

interface IScreenListProvider {
  state?: IScreenListState;
}

interface IScreenListHost {
  screenProvider?: IScreenListProvider;
}

/** The project's screen list as the ScreenDesignContextProvider holds it; empty when unattached. */
function readScreenList(sdInstance: ScreenDesignInstance): IScreenListEntry[] {
  const host: IScreenListHost = sdInstance as unknown as IScreenListHost;
  return host?.screenProvider?.state?.screenList ?? [];
}

/** Screen id → name lookup for `summarizeActionBindings`. */
export function buildActionSummaryScreenIndex(sdInstance: ScreenDesignInstance): ScreenIndex {
  const named: INamedScreen[] = [];
  for (const entry of readScreenList(sdInstance)) {
    if (typeof entry?.name === "string") named.push({ id: entry.id, name: entry.name });
  }
  return buildScreenIndex(named);
}

function truncateBindingSummary(text: string): string {
  if (text.length <= BINDING_SUMMARY_MAX_LENGTH) return text;
  return `${text.slice(0, BINDING_SUMMARY_MAX_LENGTH - 1)}…`;
}

/**
 * One short token describing what feeds a parameter: `(unset)`, `fixed:<json>`,
 * or `<category>:<source>` (e.g. `current:#12.Id`, `displayItemProp:#7.value`,
 * `screenInput:OrderId`). A read-back aid, not a serialization — the full symbol
 * is on `ui_action.get_details`.
 */
export function summarizeSymbol(symbol: ISymbol | undefined | null): string {
  if (!symbol) return "(unset)";
  const hasFixedValue: boolean = symbol.fixedValue !== undefined && symbol.fixedValue !== null;
  const isFixed: boolean =
    symbol.isFixed === true ||
    symbol.category === ESymbolCategory.fixed ||
    (symbol.category === undefined && hasFixedValue);
  if (isFixed) return truncateBindingSummary(`fixed:${JSON.stringify(symbol.fixedValue ?? null)}`);
  if (symbol.category === undefined || symbol.category === ESymbolCategory.none) return "(unset)";

  const category: string = ESymbolCategory[symbol.category] ?? String(symbol.category);
  const parts: string[] = [];
  if (symbol.diId !== undefined && symbol.diId !== null) parts.push(`#${symbol.diId}`);
  const sources: unknown[] = [
    symbol.diPropName,
    symbol.inputName,
    symbol.bagName,
    symbol.actionDefinitionObjectPath,
    symbol.fieldName
  ];
  for (const source of sources) {
    if (typeof source === "string" && source.length > 0) parts.push(source);
  }
  const detail: string = parts.length > 0 ? parts.join(".") : (symbol.label ?? "");
  return truncateBindingSummary(detail.length > 0 ? `${category}:${detail}` : category);
}

/**
 * Compact read-back of an action instance's wiring: its parameters and, for a
 * NAVIGATE, the target screen plus the screen-input bindings it carries. Without
 * this a navigate's `targetScreenInputParams` could be written but never verified.
 */
export function summarizeActionBindings(action: IAction, screenIndex: ScreenIndex | null): IActionBindingSummary {
  const summary: IActionBindingSummary = {};
  const parameters: IActionParameterSummary[] = [];

  for (const parameter of (action?.parameters ?? []) as IActionParameter[]) {
    if (typeof parameter?.name !== "string" || parameter.name.length === 0) continue;
    const fixedValue: unknown = parameter.symbol?.fixedValue;
    if (parameter.name === TO_SCREEN_ID_PARAMETER && typeof fixedValue === "string" && fixedValue.length > 0) {
      summary.targetScreenId = fixedValue;
      const targetScreenName: string | undefined = screenIndex?.byId.get(fixedValue);
      if (targetScreenName !== undefined) summary.targetScreenName = targetScreenName;
      continue;
    }
    parameters.push({ name: parameter.name, value: summarizeSymbol(parameter.symbol) });
  }
  if (parameters.length > 0) summary.parameters = parameters;

  const screenInputs: IActionParameterSummary[] = [];
  for (const input of (action?.targetScreenInputParams ?? []) as ITargetScreenInputParam[]) {
    if (typeof input?.name !== "string" || input.name.length === 0) continue;
    screenInputs.push({ name: input.name, value: summarizeSymbol(input.symbol) });
  }
  if (screenInputs.length > 0) summary.screenInputs = screenInputs;

  return summary;
}

export function getDesignerComponentEvents(
  sdInstance: ScreenDesignInstance,
  kuikaId?: string
): { kuikaId: string; availableEvents: string[]; eventCount: number; events: IEventDetail[] } | { error: string } {
  if (!kuikaId) return { error: "kuikaId is required" };

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component "${kuikaId}" not found` };

  const availableEvents = getDesignerAvailableEvents(di, kuikaId);
  const screenIndex: ScreenIndex = buildActionSummaryScreenIndex(sdInstance);
  const events: IEventDetail[] = (di.item?.events || []).map((event: IEvent) => ({
    eventName: event.eventName,
    actionCount: event.actions?.length || 0,
    actions: (event.actions || []).map((action: IAction) => ({
      id: action.id,
      label: action.label,
      description: action.description,
      actionDefinitionId: action.actionDefinitionId || null,
      ...summarizeActionBindings(action, screenIndex)
    }))
  }));

  return { kuikaId, availableEvents, eventCount: events.length, events };
}

interface IStyleResult {
  kuikaId: string;
  styles: ReturnType<typeof simplifyStyleObj>;
}

export function getDesignerComponentStyles(
  sdInstance: ScreenDesignInstance,
  kuikaId?: string
): IStyleResult | { error: string } {
  if (!kuikaId) return { error: "kuikaId is required" };

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component "${kuikaId}" not found` };

  const projectStyles = resolveProjectStyles(di.styleObj, sd.project);
  const inlineStyles = simplifyStyleObj(di.styleObj);

  const result = {
    kuikaId,
    styles: inlineStyles,
    ...(projectStyles.length > 0
      ? {
          projectStyles: projectStyles.map((ps: { id: string; name: string; section: string }) => ({
            id: ps.id,
            name: ps.name,
            section: ps.section
          }))
        }
      : {})
  };
  return result;
}

interface IMultiStyleResultItem {
  kuikaId: string;
  componentName?: string | null;
  friendlyName?: string | null;
  styles?: ReturnType<typeof simplifyStyleObj>;
  error?: string;
}

export function getDesignerMultipleComponentStyles(
  sdInstance: ScreenDesignInstance,
  input: { kuikaIds?: string[] | string }
): { count: number; results: IMultiStyleResultItem[] } | { error: string } {
  let kuikaIds = input.kuikaIds;
  if (typeof kuikaIds === "string") {
    try {
      kuikaIds = JSON.parse(kuikaIds);
    } catch {
      return { error: "kuikaIds must be an array of strings" };
    }
  }
  if (!kuikaIds || !Array.isArray(kuikaIds) || kuikaIds.length === 0) {
    return { error: "kuikaIds array is required and must not be empty" };
  }

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const results: IMultiStyleResultItem[] = [];

  for (const kuikaId of kuikaIds) {
    const di = dict[parseInt(kuikaId, 10)];
    if (!di) {
      results.push({ kuikaId, error: `Component "${kuikaId}" not found` });
      continue;
    }

    const projectStyles = resolveProjectStyles(di.styleObj, sd.project);
    const inlineStyles = simplifyStyleObj(di.styleObj);

    results.push({
      kuikaId,
      componentName: di.item?.name || null,
      friendlyName: di.friendlyName || null,
      styles: inlineStyles,
      ...(projectStyles.length > 0
        ? {
            projectStyles: projectStyles.map((ps: { id: string; name: string; section: string }) => ({
              id: ps.id,
              name: ps.name,
              section: ps.section
            }))
          }
        : {})
    });
  }

  return { count: results.length, results };
}

interface IMultiInfoResultItem extends IComponentInfoResult {
  error?: string;
}

export function getDesignerMultipleComponentInfo(
  sdInstance: ScreenDesignInstance,
  input: { kuikaIds?: string[] | string }
): { count: number; results: (IMultiInfoResultItem | { kuikaId: string; error: string })[] } | { error: string } {
  let kuikaIds = input.kuikaIds;
  if (typeof kuikaIds === "string") {
    try {
      kuikaIds = JSON.parse(kuikaIds);
    } catch {
      return { error: "kuikaIds must be an array of strings" };
    }
  }
  if (!kuikaIds || !Array.isArray(kuikaIds) || kuikaIds.length === 0) {
    return { error: "kuikaIds array is required and must not be empty" };
  }

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const results: (IMultiInfoResultItem | { kuikaId: string; error: string })[] = [];

  for (const kuikaId of kuikaIds) {
    const di = dict[parseInt(kuikaId, 10)];
    if (!di) {
      results.push({ kuikaId, error: `Component "${kuikaId}" not found` });
      continue;
    }

    const allProperties = getDesignerPropertySummary(di, sd);
    const { properties, propertiesOmitted } = capProperties(allProperties);

    results.push({
      kuikaId: di.id?.toString(),
      name: di.item.name,
      componentType: di.item.name,
      friendlyName: di.friendlyName || null,
      // `get_info` routes HERE, not to `getDesignerComponentInfo` — the two build
      // the same shape by hand and the contract channels reached only one of them,
      // so a stamped component read back as unstamped. Keep both in step.
      ...contractChannelProjection(di),
      parentId: di.parentId,
      properties,
      ...(propertiesOmitted !== undefined ? { propertiesOmitted } : {}),
      events: getDesignerEventSummary(di),
      availableEvents: getDesignerAvailableEvents(di, kuikaId),
      childCount: di.children?.length || 0,
      hasChildren: (di.children?.length || 0) > 0,
      visibility:
        di.item?.properties?.find((property: IDisplayItemProperty) => property.name === "visible")?.symbol
          ?.fixedValue ?? null,
      editability:
        di.item?.properties?.find((property: IDisplayItemProperty) => property.name === "enabled")?.symbol
          ?.fixedValue ?? null
    });
  }

  return { count: results.length, results };
}

interface IHierarchyResult {
  kuikaId: string;
  ancestorChain: IAncestorChainEntry[];
  subtree: string;
  childCount: number;
}

export function getDesignerComponentHierarchy(
  sdInstance: ScreenDesignInstance,
  input: { kuikaId?: string; maxDepth?: string | number }
): IHierarchyResult | { error: string } {
  const { kuikaId } = input;
  if (!kuikaId) return { error: "kuikaId is required" };

  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };

  const ancestorChain: IAncestorChainEntry[] = [];
  let current: IDisplayItem | undefined = di;
  while (current) {
    ancestorChain.push({
      kuikaId: current.id?.toString(),
      name: current.item?.name || "Unknown",
      friendlyName: current.friendlyName || null
    });
    if (current.parentId != null && current.parentId !== undefined) {
      current = dict[current.parentId];
    } else {
      break;
    }
  }
  ancestorChain.reverse();

  const maxDepth = Math.max(1, Math.min(20, parseInt(String(input?.maxDepth ?? 5), 10) || 5));
  const lines: string[] = [];

  function walkSubtree(node: IDisplayItem, depth: number) {
    const indent = "  ".repeat(depth);
    const name = node.item?.name || "Unknown";
    const friendly = node.friendlyName ? ` "${node.friendlyName}"` : "";
    const id = node.id != null ? ` [${node.id}]` : "";
    lines.push(`${indent}${name}${friendly}${id}`);

    const children = node.children;
    if (children && children.length > 0) {
      if (depth >= maxDepth) {
        lines.push(`${indent}  ...${children.length} children`);
      } else {
        for (const child of children) {
          walkSubtree(child, depth + 1);
        }
      }
    }
  }

  walkSubtree(di, 0);

  return {
    kuikaId,
    ancestorChain,
    subtree: lines.join("\n"),
    childCount: di.children?.length || 0
  };
}

/**
 * Type-family aliases for `find_by_type` / `find_components_by_name` lookups.
 * Each key maps to the list of canonical type names the lookup should ALSO
 * match against — covering both deprecated names and component families
 * that share a parameter-binding role (a Decimal action parameter can be
 * fed by NumberInput / Percent / Currency / Money interchangeably).
 *
 * Reasons to know — three KOA-17026 traces:
 *
 *   1. Binder queried `componentType: "DatePicker"` (deprecated name) while
 *      the canvas hosted a "Date" component. With no alias, lookup returned
 *      zero and Binder bounced "missing_component" → 60+ specialist rounds.
 *
 *   2. Binder queried `componentType: "DateInput"` (invented name) for a
 *      DateTime parameter. Plain Date alias didn't catch it → fell back to
 *      a TextArea as the date source → KayitTarihi parameter bound to
 *      `olcum-not.value` → SQL DateTime column gets text input value.
 *
 *   3. Binder queried `componentType: "NumberInput"` for a Decimal
 *      parameter ("YagOrani" — fat percentage). The canonical Kuika input
 *      for percent fields is the `Percent` component, NOT NumberInput.
 *      NumberInput-only lookup missed the Percent input → Binder bound
 *      YagOrani to `default:Empty` → decimal column ships as null.
 *
 * Substring match is used downstream (lowered.includes(needle)), so each
 * needle here is the lowercase form of the actual component type name.
 */
const TYPE_FAMILY_ALIASES: Record<string, readonly string[]> = {
  // Date input family — non-canonical names the LLM tends to invent /
  // remember from older docs map onto the canonical "Date" component.
  datepicker: ["date"],
  dateinput: ["date"],
  picker: ["date"],
  // Numeric input family — a Decimal / Number action parameter may be
  // sourced from any of NumberInput / Percent / Currency / Money. Querying
  // by NumberInput alone would silently miss Percent (yağ oranı) and
  // Currency (tutar / fiyat) form inputs.
  numberinput: ["percent", "currency", "money"],
  numericinput: ["numberinput", "percent", "currency", "money"],
  decimalinput: ["numberinput", "percent", "currency", "money"],
  number: ["numberinput", "percent", "currency", "money"]
};

export function findDesignerComponentsByName(
  sdInstance: ScreenDesignInstance,
  componentName?: string
): { count: number; components: IFoundComponent[] } | { error: string } {
  if (!componentName) return { error: "componentName is required" };

  const sd = getValues(sdInstance);
  const screenDesign = getScreenDesign(sd);
  if (!screenDesign || !screenDesign.displayItems) {
    return { error: "No screen design loaded" };
  }

  const results: IFoundComponent[] = [];
  const seen = new Set<string>();
  const primaryNeedle = componentName.toLowerCase();
  const needles = [primaryNeedle];
  const expansions = TYPE_FAMILY_ALIASES[primaryNeedle];
  if (expansions) {
    for (const expansion of expansions) {
      if (!needles.includes(expansion)) needles.push(expansion);
    }
  }

  function walk(di: IDisplayItem) {
    const lowered = di.item?.name?.toLowerCase();
    if (lowered) {
      const key = di.id?.toString() ?? "";
      if (!seen.has(key) && needles.some((n) => lowered.includes(n))) {
        seen.add(key);
        const typeName: string | undefined = di.item?.name;
        results.push({
          kuikaId: di.id?.toString(),
          name: typeName,
          componentType: typeName,
          friendlyName: di.friendlyName || null,
          ...contractChannelProjection(di),
          dataSourceAlias: readDataSourceAlias(di)
        });
      }
    }
    if (di.children) {
      for (const child of di.children) {
        walk(child);
      }
    }
  }

  for (const di of screenDesign.displayItems) {
    walk(di);
  }

  return { count: results.length, components: results };
}

/**
 * KOA-17394 (perf) — enumerate EVERY component on the active screen in a
 * single synchronous pass over the flat screen dictionary (the same source
 * `getDesignerMultipleComponentInfo` reads). This is the canvas-inventory
 * layer's Pass-1 discovery primitive: it replaces the deterministic
 * Wirer/Binder's former ~115-way `find_by_type` fan-out — one recursive tree
 * walk per component type, each a separate browser round-trip — with one call.
 *
 * The shape mirrors `findDesignerComponentsByName` (`IFoundComponent`) so the
 * existing Pass-1 parser in `canvas-inventory.ts` consumes it unchanged.
 *
 * Source-of-truth note: the dictionary is rebuilt from the tree on every
 * `setScreenDesign` (`CommonHelperFunctions.createScreenDesignDict`, a BFS over
 * `displayItems` into a fresh `{}`), so it never retains detached/stale nodes —
 * `Object.values(dict)` is exactly the live tree's node set. Enumeration order
 * is V8's ascending-numeric-key order; the canvas-inventory layer re-sorts by
 * label-resolution priority, so the order here is not load-bearing.
 */
export function listAllDesignerComponents(
  sdInstance: ScreenDesignInstance
): { count: number; components: IFoundComponent[] } | { error: string } {
  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);
  const components: IFoundComponent[] = [];
  for (const di of Object.values(dict)) {
    if (!di || !di.item) continue;
    const typeName: string | undefined = di.item.name;
    components.push({
      kuikaId: di.id?.toString(),
      name: typeName,
      componentType: typeName,
      friendlyName: di.friendlyName || null,
      ...contractChannelProjection(di),
      dataSourceAlias: readDataSourceAlias(di)
    });
  }
  return { count: components.length, components };
}

// -- Search --

interface ISearchInput {
  query?: string;
  maxResults?: number | string;
}

interface ISearchResultItem {
  kuikaId: string;
  componentType: string;
  friendlyName: string | null;
  parentId: string | null;
  parentType: string | null;
  matchedOn: "type" | "name" | "property";
  matchedProperty?: string;
  matchedValue?: string;
  keyProperties: Array<{ name: string; value: unknown }>;
}

interface ISearchResult {
  count: number;
  results: ISearchResultItem[];
  truncated: boolean;
}

const SEARCH_MAX_KEY_PROPS = 5;
const SEARCH_VALUE_TRUNCATE = 50;

function buildKeyPropertySnapshot(di: IDisplayItem): Array<{ name: string; value: unknown }> {
  if (!di.item?.properties) return [];
  const result: Array<{ name: string; value: unknown }> = [];
  for (const prop of di.item.properties) {
    if ((prop as IProperty).selectedType === 3) continue;
    if (!KEY_PROPERTY_NAMES.has(prop.name)) continue;
    const val = (prop as IDisplayItemProperty).symbol?.fixedValue ?? (prop as IDisplayItemProperty).value ?? null;
    if (val == null || val === "") continue;
    const display =
      typeof val === "string" && val.length > SEARCH_VALUE_TRUNCATE ? val.slice(0, SEARCH_VALUE_TRUNCATE) + "…" : val;
    result.push({ name: prop.name, value: display });
    if (result.length >= SEARCH_MAX_KEY_PROPS) break;
  }
  return result;
}

export function searchDesignerComponents(
  sdInstance: ScreenDesignInstance,
  input: ISearchInput
): ISearchResult | { error: string } {
  if (!input.query) return { error: "query is required" };

  let regex: RegExp;
  try {
    regex = new RegExp(input.query, "i");
  } catch {
    regex = new RegExp(input.query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
  }

  const sd = getValues(sdInstance);
  const screenDesign = getScreenDesign(sd);
  if (!screenDesign || !screenDesign.displayItems) {
    return { error: "No screen design loaded" };
  }
  const dict = getScreenDesignDictionary(sd);
  const maxResults = Math.max(1, Math.min(30, parseInt(String(input.maxResults ?? 10), 10) || 10));

  const matches: ISearchResultItem[] = [];
  let totalCount = 0;

  function walk(di: IDisplayItem) {
    if (!di || !di.item) {
      if (di?.children) for (const child of di.children) walk(child);
      return;
    }

    let matchedOn: "type" | "name" | "property" | null = null;
    let matchedProperty: string | undefined;
    let matchedValue: string | undefined;

    if (di.item.name && regex.test(di.item.name)) {
      matchedOn = "type";
    } else if (di.friendlyName && regex.test(di.friendlyName)) {
      matchedOn = "name";
    } else if (di.item.properties) {
      for (const prop of di.item.properties) {
        if ((prop as IProperty).selectedType === 3) continue;
        const sym = (prop as IDisplayItemProperty).symbol;
        if (sym?.category !== undefined && sym.category !== 4) continue;
        const val = sym?.fixedValue;
        if (val == null || typeof val === "object") continue;
        if (regex.test(String(val))) {
          matchedOn = "property";
          matchedProperty = prop.name;
          matchedValue = String(val).slice(0, SEARCH_VALUE_TRUNCATE);
          break;
        }
      }
    }

    if (matchedOn) {
      totalCount++;
      if (matches.length < maxResults) {
        const parentDi = di.parentId != null ? dict[di.parentId] : undefined;
        matches.push({
          kuikaId: String(di.id),
          componentType: di.item.name || "Unknown",
          friendlyName: di.friendlyName || null,
          parentId: di.parentId != null ? String(di.parentId) : null,
          parentType: parentDi?.item?.name || null,
          matchedOn,
          ...(matchedProperty ? { matchedProperty } : {}),
          ...(matchedValue ? { matchedValue } : {}),
          keyProperties: buildKeyPropertySnapshot(di)
        });
      }
    }

    if (di.children) {
      for (const child of di.children) walk(child);
    }
  }

  for (const di of screenDesign.displayItems) {
    walk(di);
  }

  return { count: totalCount, results: matches, truncated: totalCount > matches.length };
}

// -- Completeness check --

const DATASOURCE_COMPONENTS = new Set([
  "Table",
  "StaticTable",
  "GalleryView",
  "FlexGrid",
  "HorizontalGrid",
  "KanbanBoard",
  "Calendar"
]);

const BUTTON_COMPONENTS = new Set(["Button", "FloatButton"]);

const FORM_INPUT_COMPONENTS = new Set([
  "TextInput",
  "NumberInput",
  "SelectBox",
  "MultiSelect",
  "AutoComplete",
  "Date",
  "DateTime",
  "DateRange",
  "Checkbox",
  "Switch",
  "RadioGroup",
  "RadioButtonGroup",
  "TextArea",
  "Password",
  "Email",
  "Url",
  "Currency",
  "Percent",
  "Slider",
  "ColorPicker"
]);

const STRUCTURAL_COMPONENTS = new Set(["Body", "Header", "Footer", "Row", "Col", "VerticalStack", "HorizontalStack"]);

function isPropertyBound(prop: IDisplayItemProperty): boolean {
  const cat = prop.symbol?.category;
  return (cat !== undefined && cat !== 4 && cat !== 0) || !!prop.symbol?.actionDefinitionId;
}

function findNonEventProperty(props: IDisplayItemProperty[], name: string): IDisplayItemProperty | undefined {
  return props.find((p) => p.name === name && (p as IProperty).selectedType !== 3);
}

function checkDatasourceBinding(
  kuikaId: string,
  name: string,
  friendly: string | null,
  props: IDisplayItemProperty[],
  issues: ICompletenessIssue[]
): void {
  const dsProp = props.find((p) => p.name.toLowerCase() === "datasource" && (p as IProperty).selectedType !== 3);

  if (!dsProp || !isPropertyBound(dsProp)) {
    issues.push({
      kuikaId,
      componentType: name,
      friendlyName: friendly,
      severity: "high",
      issue: `${name} "${friendly || kuikaId}" has no datasource binding`,
      suggestion: `Bind the datasource of ${friendly || name} [${kuikaId}] to a data action (e.g., GetList query)`
    });
  }
}

/** How far up the tree a button's navigation group is searched for. */
const NAVIGATION_GROUP_MAX_ANCESTOR_DEPTH: number = 4;
/** Fewest OTHER navigating entries that make a set of buttons read as a menu. */
const NAVIGATION_GROUP_MIN_OTHER_ENTRIES: number = 2;

function collectButtonDescendants(di: IDisplayItem, out: IDisplayItem[]): void {
  for (const child of di.children ?? []) {
    if (BUTTON_COMPONENTS.has(child.item?.name || "")) out.push(child);
    collectButtonDescendants(child, out);
  }
}

/** The fixed `toScreenId` of the button's onClick NAVIGATE, or null when it does not navigate. */
function readButtonNavigateTarget(di: IDisplayItem): string | null {
  const onClick: IEvent | undefined = (di.item?.events ?? []).find((e: IEvent) => e.eventName === "onClick");
  for (const action of onClick?.actions ?? []) {
    if (action.label !== "NAVIGATE") continue;
    const parameter: IActionParameter | undefined = action.parameters?.find(
      (p: IActionParameter) => p.name === TO_SCREEN_ID_PARAMETER
    );
    const target: unknown = parameter?.symbol?.fixedValue;
    if (typeof target === "string" && target.length > 0) return target;
  }
  return null;
}

/**
 * A navigation menu lists every screen, including the one it is rendered on; the
 * entry for the OPEN screen has nowhere to go, so an empty onClick there is
 * complete, not a defect. Recognised structurally, never by caption: the nearest
 * ancestor that groups this button with other buttons must hold at least
 * `NAVIGATION_GROUP_MIN_OTHER_ENTRIES` of them, EVERY one of those must NAVIGATE,
 * each to a DISTINCT screen, and none to the current screen (if one did, that one
 * is the current-screen entry and this button is genuinely unwired).
 */
function isCurrentScreenNavigationEntry(
  di: IDisplayItem,
  dict: Record<number, IDisplayItem>,
  currentScreenId: string | undefined
): boolean {
  if (!currentScreenId) return false;

  let ancestor: IDisplayItem | undefined = di.parentId != null ? dict[di.parentId] : undefined;
  for (let depth = 0; depth < NAVIGATION_GROUP_MAX_ANCESTOR_DEPTH && ancestor; depth++) {
    const buttons: IDisplayItem[] = [];
    collectButtonDescendants(ancestor, buttons);
    const others: IDisplayItem[] = buttons.filter((button: IDisplayItem) => button.id !== di.id);
    if (others.length > 0) {
      if (others.length < NAVIGATION_GROUP_MIN_OTHER_ENTRIES) return false;
      const targets: Set<string> = new Set<string>();
      for (const other of others) {
        const target: string | null = readButtonNavigateTarget(other);
        if (target === null || target === currentScreenId) return false;
        targets.add(target);
      }
      return targets.size === others.length;
    }
    ancestor = ancestor.parentId != null ? dict[ancestor.parentId] : undefined;
  }
  return false;
}

/** Symbol categories that read a value straight off a component on this screen. */
const COMPONENT_SOURCE_CATEGORIES: ReadonlySet<ESymbolCategory> = new Set<ESymbolCategory>([
  ESymbolCategory.displayItem,
  ESymbolCategory.displayItemProp
]);

function collectParameterSourceIds(parameters: IActionParameter[] | undefined, out: Set<number>): void {
  for (const parameter of parameters ?? []) {
    const symbol: ISymbol | undefined = parameter?.symbol;
    if (symbol && COMPONENT_SOURCE_CATEGORIES.has(symbol.category) && symbol.diId !== undefined) {
      out.add(Number(symbol.diId));
    }
    collectParameterSourceIds(parameter?.parameters, out);
  }
}

function collectActionSourceIds(actions: IAction[] | undefined, out: Set<number>): void {
  for (const action of actions ?? []) {
    collectParameterSourceIds(action.parameters, out);
    for (const input of action.targetScreenInputParams ?? []) {
      const symbol: ISymbol | undefined = input?.symbol;
      if (symbol && COMPONENT_SOURCE_CATEGORIES.has(symbol.category) && symbol.diId !== undefined) {
        out.add(Number(symbol.diId));
      }
    }
    collectActionSourceIds(action.ifThenElseData?.ifActions, out);
    collectActionSourceIds(action.ifThenElseData?.elseActions, out);
    collectActionSourceIds(action.forEachData?.actions, out);
  }
}

/**
 * Ids of components whose value some action on the screen reads as a parameter
 * (or hands to a navigate's target screen). An input on a create form is unbound
 * by design — what makes it complete is that a save/mutation action consumes it.
 */
function collectActionConsumedComponentIds(dict: Record<number, IDisplayItem>): Set<number> {
  const consumed: Set<number> = new Set<number>();
  for (const di of Object.values(dict)) {
    for (const event of di?.item?.events ?? []) {
      collectActionSourceIds(event.actions, consumed);
    }
  }
  return consumed;
}

function checkButtonEvent(
  kuikaId: string,
  name: string,
  friendly: string | null,
  events: IEvent[],
  issues: ICompletenessIssue[],
  likelyCurrentScreenEntry: boolean
): void {
  const onClickEvent = events.find((e) => e.eventName === "onClick");
  if (!onClickEvent || !onClickEvent.actions || onClickEvent.actions.length === 0) {
    if (likelyCurrentScreenEntry) {
      issues.push({
        kuikaId,
        componentType: name,
        friendlyName: friendly,
        severity: "low",
        likelyCurrentScreenEntry: true,
        issue: `${name} "${friendly || kuikaId}" has no onClick action, but it looks like the navigation-menu entry of the open screen`,
        suggestion: `No action needed if ${friendly || name} [${kuikaId}] is the menu entry for this screen; otherwise add an onClick event to it`
      });
      return;
    }
    issues.push({
      kuikaId,
      componentType: name,
      friendlyName: friendly,
      severity: "high",
      issue: `${name} "${friendly || kuikaId}" has no onClick action`,
      suggestion: `Add an onClick event to ${friendly || name} [${kuikaId}] (e.g., navigation, form submit, or data action)`
    });
  }
}

function checkFormInputBinding(
  kuikaId: string,
  name: string,
  friendly: string | null,
  props: IDisplayItemProperty[],
  issues: ICompletenessIssue[]
): void {
  const valueProp = findNonEventProperty(props, "value");
  if (!valueProp) return;

  if (!isPropertyBound(valueProp)) {
    issues.push({
      kuikaId,
      componentType: name,
      friendlyName: friendly,
      severity: "medium",
      issue: `${name} "${friendly || kuikaId}" value is not bound to data`,
      suggestion: `Bind the value property of ${friendly || name} [${kuikaId}] to a form data field or action result`
    });
  }
}

function checkDefaultLabelText(
  kuikaId: string,
  name: string,
  friendly: string | null,
  props: IDisplayItemProperty[],
  issues: ICompletenessIssue[]
): void {
  const valueProp = findNonEventProperty(props, "value");
  if (!valueProp) return;

  if (isPropertyBound(valueProp)) return;

  const currentValue = valueProp.symbol?.fixedValue ?? valueProp.value;
  if (currentValue === "label" || currentValue === "Label" || currentValue == null || currentValue === "") {
    issues.push({
      kuikaId,
      componentType: name,
      friendlyName: friendly,
      severity: "medium",
      issue: `Label "${friendly || kuikaId}" has default text "${String(currentValue || "empty")}"`,
      suggestion: `Set a meaningful value for Label [${kuikaId}] or bind it to data`
    });
  }
}

function checkImageSource(
  kuikaId: string,
  name: string,
  friendly: string | null,
  props: IDisplayItemProperty[],
  issues: ICompletenessIssue[]
): void {
  const valueProp = findNonEventProperty(props, "value");
  if (!valueProp) return;

  if (isPropertyBound(valueProp)) return;

  const currentValue = valueProp.symbol?.fixedValue ?? valueProp.value;
  if (!currentValue || currentValue === "") {
    issues.push({
      kuikaId,
      componentType: name,
      friendlyName: friendly,
      severity: "low",
      issue: `${name} "${friendly || kuikaId}" has no image source set`,
      suggestion: `Set the value of ${friendly || name} [${kuikaId}] to an image URL or bind it to data`
    });
  }
}

function checkButtonActionParameterBindings(
  kuikaId: string,
  name: string,
  friendly: string | null,
  events: IEvent[],
  issues: ICompletenessIssue[]
): void {
  const onClick = events.find((e) => e.eventName === "onClick");
  if (!onClick || !onClick.actions || onClick.actions.length === 0) return;

  for (const action of onClick.actions) {
    if (action.label === "NAVIGATE") continue;
    if (!action.parameters || action.parameters.length === 0) continue;

    const unboundCount = action.parameters.filter(
      (p) => !p.symbol || p.symbol.category === ESymbolCategory.none
    ).length;

    if (unboundCount === action.parameters.length) {
      issues.push({
        kuikaId,
        componentType: name,
        friendlyName: friendly,
        severity: "high",
        issue: `${name} "${friendly || kuikaId}" action "${action.label}" has ${unboundCount} unbound parameter${unboundCount > 1 ? "s" : ""}`,
        suggestion: `Bind form input values to the "${action.label}" action parameters on ${friendly || name} [${kuikaId}] using set_multiple_parameter_bindings`
      });
    }
  }
}

const SCREEN_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function checkNavigationParameters(
  kuikaId: string,
  name: string,
  friendly: string | null,
  events: IEvent[],
  issues: ICompletenessIssue[],
  screenList: IScreenListEntry[]
): void {
  const onClick = events.find((e) => e.eventName === "onClick");
  if (!onClick || !onClick.actions || onClick.actions.length === 0) return;

  // Build lookup sets once per button call. Small cost, makes the
  // per-value checks O(1).
  const knownIds = new Set<string>();
  const knownNamesLower = new Set<string>();
  for (const s of screenList) {
    if (typeof s.id === "string" && s.id.length > 0) knownIds.add(s.id);
    if (typeof s.name === "string" && s.name.length > 0) knownNamesLower.add(s.name.toLowerCase());
  }

  for (const action of onClick.actions) {
    if (action.label !== "NAVIGATE") continue;

    const toScreenIdParam = action.parameters?.find((p) => p.name === "toScreenId");
    const targetValue = toScreenIdParam?.symbol?.fixedValue;

    if (targetValue === undefined || targetValue === null || targetValue === "") {
      issues.push({
        kuikaId,
        componentType: name,
        friendlyName: friendly,
        severity: "high",
        issue: `NAVIGATE action on ${name} "${friendly || kuikaId}" has no target screen set`,
        suggestion: `Set the toScreenId parameter of the NAVIGATE action on ${friendly || name} [${kuikaId}] to the target screen's ID`
      });
      continue;
    }

    const targetStr = typeof targetValue === "string" ? targetValue : String(targetValue);

    // GUID: must resolve to a real screen in the project. A stale GUID
    // (e.g., a screen deleted after the action was wired) reads as a
    // silent broken nav — flag it so the post-pass or the user can fix.
    if (SCREEN_ID_PATTERN.test(targetStr)) {
      if (knownIds.size > 0 && !knownIds.has(targetStr)) {
        issues.push({
          kuikaId,
          componentType: name,
          friendlyName: friendly,
          severity: "high",
          issue: `NAVIGATE action on ${name} "${friendly || kuikaId}" has no target screen set (id "${targetStr}" does not match any existing screen)`,
          suggestion: `Set the toScreenId parameter of the NAVIGATE action on ${friendly || name} [${kuikaId}] to a valid screen's ID`
        });
      }
      continue;
    }

    // Non-GUID: only acceptable if it matches a known screen name. A
    // literal name is what the Wirer emits before cross-screen nav
    // post-pass resolves it; flagging unresolved names lets the post-pass
    // catch them and rewrite to the real screenId.
    if (knownNamesLower.size > 0 && !knownNamesLower.has(targetStr.toLowerCase())) {
      issues.push({
        kuikaId,
        componentType: name,
        friendlyName: friendly,
        severity: "high",
        issue: `NAVIGATE action on ${name} "${friendly || kuikaId}" has no target screen set (value "${targetStr}" is not a screen ID and does not match any existing screen name)`,
        suggestion: `Set the toScreenId parameter of the NAVIGATE action on ${friendly || name} [${kuikaId}] to a valid screen's ID`
      });
    }
  }
}

function hasFieldBindingDescendant(di: IDisplayItem): boolean {
  if (!di.children || di.children.length === 0) return false;
  for (const child of di.children) {
    const props = (child.item?.properties || []) as IDisplayItemProperty[];
    const valueProp = props.find((p) => p.name === "value" && (p as IProperty).selectedType !== 3);
    if (valueProp) {
      const cat = valueProp.symbol?.category;
      if (cat === ESymbolCategory.current || cat === ESymbolCategory.actionField) {
        return true;
      }
    }
    if (hasFieldBindingDescendant(child)) return true;
  }
  return false;
}

function checkTableColumnBindings(dict: Record<number, IDisplayItem>, issues: ICompletenessIssue[]): void {
  for (const di of Object.values(dict)) {
    if (!di || !di.item) continue;
    const name = di.item.name || "";
    if (!DATASOURCE_COMPONENTS.has(name)) continue;

    const props = (di.item.properties || []) as IDisplayItemProperty[];
    const dsProp = props.find((p) => p.name.toLowerCase() === "datasource" && (p as IProperty).selectedType !== 3);
    if (!dsProp || !isPropertyBound(dsProp)) continue;

    if (!hasFieldBindingDescendant(di)) {
      const friendly = di.friendlyName || null;
      issues.push({
        kuikaId: String(di.id),
        componentType: name,
        friendlyName: friendly,
        severity: "medium",
        issue: `${name} "${friendly || di.id}" has datasource but no column bindings`,
        suggestion: `Bind column children of ${friendly || name} [${di.id}] to action result fields using {current.FieldName}`
      });
    }
  }
}

function checkPageInitActions(
  dict: Record<number, IDisplayItem>,
  hasDatasourceComponents: boolean,
  issues: ICompletenessIssue[]
): void {
  if (!hasDatasourceComponents) return;

  const bodyDi = dict[-1];
  if (!bodyDi) return;

  const pageInitEvent = bodyDi.item?.events?.find((e: IEvent) => e.eventName === "pageInit");
  const pageInitDefIds = new Set<string>(
    (pageInitEvent?.actions ?? [])
      .map((a) => (typeof a.actionDefinitionId === "string" ? a.actionDefinitionId : ""))
      .filter((id) => id.length > 0)
  );

  // KOA-17564 — a datasource component whose action is fired by a button
  // (onClick) is legitimately initialized WITHOUT a pageInit loader: a
  // filter / search table loads on the "Filtrele" / "Ara" click and is
  // intentionally empty at open. Collect every action attached to an onClick
  // anywhere on the screen so such tables are not mis-flagged as missing a
  // page initializer (which would bounce the pipeline back to re-pin the
  // parameterized filter onto pageInit — exactly what we removed).
  const onClickDefIds = new Set<string>();
  for (const di of Object.values(dict)) {
    for (const evt of di?.item?.events ?? []) {
      if (evt.eventName !== "onClick") continue;
      for (const action of evt.actions ?? []) {
        if (typeof action.actionDefinitionId === "string" && action.actionDefinitionId.length > 0) {
          onClickDefIds.add(action.actionDefinitionId);
        }
      }
    }
  }

  // Flag only when a BOUND datasource component's action is attached NOWHERE
  // (neither pageInit nor any onClick) — that genuinely renders empty forever.
  // If every bound datasource action is covered by pageInit or a button, the
  // screen is complete even with an empty pageInit. An UNBOUND datasource is
  // reported separately by checkDatasourceBinding; here it only falls back to
  // the legacy "empty pageInit" signal when its action def is unresolvable.
  let hasUninitializedDatasource = false;
  for (const di of Object.values(dict)) {
    if (!di || !di.item) continue;
    if (!DATASOURCE_COMPONENTS.has(di.item.name || "")) continue;
    const props = (di.item.properties || []) as IDisplayItemProperty[];
    const dsProp = props.find((p) => p.name.toLowerCase() === "datasource" && (p as IProperty).selectedType !== 3);
    if (!dsProp || !isPropertyBound(dsProp)) continue;
    const defId = dsProp.symbol?.actionDefinitionId;
    if (typeof defId === "string" && defId.length > 0) {
      if (!pageInitDefIds.has(defId) && !onClickDefIds.has(defId)) {
        hasUninitializedDatasource = true;
        break;
      }
    } else if (pageInitDefIds.size === 0) {
      hasUninitializedDatasource = true;
      break;
    }
  }

  if (hasUninitializedDatasource) {
    issues.push({
      kuikaId: "-1",
      componentType: "Body",
      friendlyName: "Page",
      severity: "high",
      issue: "Screen has data-displaying components but no pageInit actions",
      suggestion:
        "Add pageInit actions on the Body [-1] to load data when the screen opens (e.g., call GetList datasource)"
    });
  }
}

function calculateCompletenessScore(issues: ICompletenessIssue[]): number {
  let score = 100;
  for (const issue of issues) {
    if (issue.severity === "high") score -= 15;
    else if (issue.severity === "medium") score -= 7;
    else score -= 3;
  }
  return Math.max(0, Math.min(100, score));
}

export function checkDesignerScreenCompleteness(
  sdInstance: ScreenDesignInstance
): ICompletenessResult | { error: string } {
  const sd = getValues(sdInstance);
  const dict = getScreenDesignDictionary(sd);

  if (!dict || Object.keys(dict).length === 0) {
    return { error: "No screen design loaded" };
  }

  // Screen list is used by checkNavigationParameters to validate that
  // NAVIGATE.toScreenId points at a real screen. Pulled from the
  // ScreenDesignContextProvider once and reused for every button.
  const screenList: IScreenListEntry[] = readScreenList(sdInstance);

  const currentScreenId: string | undefined = getScreenDesign(sd)?.id;
  const consumedComponentIds: Set<number> = collectActionConsumedComponentIds(dict);

  const issues: ICompletenessIssue[] = [];
  let componentCount = 0;
  let hasDatasourceComponents = false;

  for (const di of Object.values(dict)) {
    if (!di || !di.item) continue;

    const name = di.item.name || "";
    if (!name || STRUCTURAL_COMPONENTS.has(name)) continue;

    componentCount++;
    const kuikaId = String(di.id);
    const friendly = di.friendlyName || null;
    const props = (di.item.properties || []) as IDisplayItemProperty[];
    const events = di.item.events || [];

    if (DATASOURCE_COMPONENTS.has(name)) {
      hasDatasourceComponents = true;
      checkDatasourceBinding(kuikaId, name, friendly, props, issues);
    }

    if (BUTTON_COMPONENTS.has(name)) {
      const likelyCurrentScreenEntry: boolean = isCurrentScreenNavigationEntry(di, dict, currentScreenId);
      checkButtonEvent(kuikaId, name, friendly, events, issues, likelyCurrentScreenEntry);
      checkButtonActionParameterBindings(kuikaId, name, friendly, events, issues);
      checkNavigationParameters(kuikaId, name, friendly, events, issues, screenList);
    }

    if (FORM_INPUT_COMPONENTS.has(name) && !consumedComponentIds.has(Number(di.id))) {
      checkFormInputBinding(kuikaId, name, friendly, props, issues);
    }

    if (name === "Label") {
      checkDefaultLabelText(kuikaId, name, friendly, props, issues);
    }

    if (name === "Image" || name === "Thumbnail") {
      checkImageSource(kuikaId, name, friendly, props, issues);
    }
  }

  checkPageInitActions(dict, hasDatasourceComponents, issues);
  checkTableColumnBindings(dict, issues);

  // Advisory rows are reported but never counted: not in `complete`, `score`, or the totals.
  const countedIssues: ICompletenessIssue[] = issues.filter(
    (i: ICompletenessIssue) => i.likelyCurrentScreenEntry !== true
  );
  const advisoryCount: number = issues.length - countedIssues.length;
  const score = calculateCompletenessScore(countedIssues);

  const highCount = countedIssues.filter((i) => i.severity === "high").length;
  const medCount = countedIssues.filter((i) => i.severity === "medium").length;
  const lowCount = countedIssues.filter((i) => i.severity === "low").length;
  const parts: string[] = [];
  if (highCount > 0) parts.push(`${highCount} critical`);
  if (medCount > 0) parts.push(`${medCount} warning`);
  if (lowCount > 0) parts.push(`${lowCount} minor`);

  const countedSummary: string =
    countedIssues.length === 0
      ? "Screen build is complete. All components are properly configured."
      : `Found ${parts.join(", ")} issue${countedIssues.length > 1 ? "s" : ""} across ${componentCount} components.`;
  const advisorySummary: string =
    advisoryCount > 0 ? ` ${advisoryCount} advisory note${advisoryCount > 1 ? "s" : ""} (no action required).` : "";

  return {
    complete: countedIssues.length === 0,
    score,
    componentCount,
    issues,
    summary: `${countedSummary}${advisorySummary}`
  };
}
